import { useState, useEffect } from "react";
import {
  HiOutlineArrowRight, HiOutlineCheck, HiOutlineDocumentText,
  HiOutlineInformationCircle, HiOutlineSparkles, HiOutlineCalendar,
  HiOutlineClock, HiOutlineMapPin, HiOutlineCheckCircle, HiOutlineXCircle,
  HiOutlineQueueList,
} from "react-icons/hi2";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import PortalLayout from "./PortalLayout";
import { portalAPI } from "./portalApi";
import "./JourneyPage.css";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : null);

// Real pipeline-driven timeline — single source of truth for where the
// applicant actually stands, replacing the older status-only (New/Reviewing/
// Accepted/Rejected) timeline that could disagree with the real clearance
// state shown elsewhere in the portal and admin panel.
function buildPipelineJourney(app, offer) {
  const queueStatus = offer?.queue_status;
  const steps = [
    {
      key: "submitted",
      label: "Application Submitted",
      done: true,
      date: fmt(app?.created_at),
      sub: "Your application was received and is on file.",
    },
    {
      key: "prescreening",
      label: "Prescreening",
      done: !!app?.prescreening_at,
      date: app?.prescreening_at ? fmt(app.prescreening_at) : null,
      sub: app?.prescreening_at
        ? "Your prescreening questionnaire is complete."
        : "Complete your prescreening questionnaire to move forward.",
    },
    {
      key: "screening",
      label: "Screening Cleared",
      done: !!app?.screening_cleared,
      date: app?.screening_cleared_at ? fmt(app.screening_cleared_at) : null,
      sub: app?.screening_cleared
        ? "Our team has cleared your screening."
        : "Our team is reviewing your prescreening responses.",
    },
    {
      key: "medical",
      label: "Medical Cleared",
      done: !!app?.medical_cleared,
      date: app?.medical_cleared_at ? fmt(app.medical_cleared_at) : null,
      sub: app?.medical_cleared
        ? "Your medical clearance is complete."
        : app?.medical_submitted_at
          ? "Documents submitted — awaiting review."
          : "Submit your medical documents once screening is cleared.",
    },
    {
      key: "queue",
      label: "Retreat Queue",
      done: ["queued", "offered", "assigned", "alumni"].includes(queueStatus),
      date: app?.queued_at ? fmt(app.queued_at) : null,
      sub: queueStatus === "queued"
        ? "Waiting for the next available retreat spot."
        : "You'll join the queue automatically once fully cleared.",
    },
    {
      key: "retreat",
      label: queueStatus === "alumni" ? "Retreat Completed" : "Your Retreat",
      done: queueStatus === "assigned" || queueStatus === "alumni",
      date: null,
      sub: queueStatus === "alumni"
        ? "You attended your retreat — thank you for being part of Forge."
        : queueStatus === "assigned"
          ? "Your spot is confirmed."
          : "Seven transformational days in the heart of Colombia.",
    },
  ];
  const activeIdx = steps.findIndex((s) => !s.done);
  return steps.map((s, i) => ({ ...s, active: i === activeIdx }));
}

const STEP_GUIDANCE = {
  submitted:    { desc: "Our team will review your application — usually within 1–2 weeks." },
  prescreening: { desc: "Head to the Prescreening page to complete your questionnaire — it only takes a few minutes." },
  screening:    { desc: "Our team is reviewing your prescreening responses. We'll clear you once that's complete." },
  medical:      { desc: "Submit your medical clearance documents on the Medical Clearance page." },
  queue:        { desc: "You're in line — we'll email you the moment a retreat spot opens up for you." },
  retreat:      { desc: "Watch your email and Messages tab for preparation details ahead of your retreat." },
};

export default function JourneyPage() {
  const [app,         setApp]         = useState(null);
  const [offer,       setOffer]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [responding,  setResponding]  = useState(false);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      portalAPI.application().catch(() => null),
      portalAPI.getOffer().catch(() => null),
    ]).then(([appRes, offerRes]) => {
      setApp(appRes?.data || null);
      setOffer(offerRes?.data || null);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const handleOfferResponse = async (response) => {
    setResponding(true);
    try {
      const res = await portalAPI.respondToOffer(response);
      toast.success(res?.message || (response === "accept" ? "Confirmed!" : "No problem — you're back in the queue."));
      fetchAll();
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setResponding(false);
    }
  };

  const journey = buildPipelineJourney(app, offer);
  const activeStep = journey.find((s) => s.active) || journey[journey.length - 1];
  const guidance = STEP_GUIDANCE[activeStep.key] || STEP_GUIDANCE.submitted;

  return (
    <PortalLayout eyebrow="My Journey" pageTitle="Your Journey" quote="The journey of a thousand miles begins with one step." quoteAuthor="Lao Tzu">
      <p className="jrn__sub">Every step forward brings you closer to transformation. Here's where you are on your path.</p>

      {/* ── Booking window open — book in the booking flow ── */}
      {offer?.queue_status === "offered" && (
        <div className="pc jrn__offer-card jrn__offer-card--pending">
          <div className="jrn__offer-icon"><HiOutlineSparkles/></div>
          <p className="jrn__offer-eyebrow">Your Booking Window Is Open</p>
          <h2 className="jrn__offer-title">
            {offer.openSelection ? "Choose your retreat" : offer.block_name}
          </h2>
          {!offer.openSelection && (
            <p className="jrn__offer-dates">
              <HiOutlineCalendar/> {fmt(offer.block_start_date)} – {fmt(offer.block_end_date)}
            </p>
          )}
          {!offer.openSelection && offer.block_location && (
            <p className="jrn__offer-location"><HiOutlineMapPin/> {offer.block_location}</p>
          )}
          <p className="jrn__offer-expiry">
            Pick your dates, pathway, room and activity by <strong>{fmt(offer.offer_expires_at)}</strong>.
          </p>
          <div className="jrn__offer-actions">
            <button type="button" className="jrn__offer-decline" disabled={responding}
              onClick={() => handleOfferResponse("decline")}>
              <HiOutlineXCircle/> {offer.openSelection ? "None of these dates work" : "Can't make this date"}
            </button>
            <Link to="/portal/book" className="jrn__offer-accept">
              <HiOutlineCheckCircle/> Book my retreat <HiOutlineArrowRight/>
            </Link>
          </div>
        </div>
      )}

      {/* ── Confirmed retreat ── */}
      {offer?.queue_status === "assigned" && (
        <div className="pc jrn__offer-card jrn__offer-card--confirmed">
          <div className="jrn__offer-icon jrn__offer-icon--confirmed"><HiOutlineCheckCircle/></div>
          <p className="jrn__offer-eyebrow">You're Confirmed!</p>
          <h2 className="jrn__offer-title">{offer.block_name}</h2>
          <p className="jrn__offer-dates">
            <HiOutlineCalendar/> {fmt(offer.block_start_date)} – {fmt(offer.block_end_date)}
          </p>
          {offer.block_location && (
            <p className="jrn__offer-location"><HiOutlineMapPin/> {offer.block_location}</p>
          )}
          <p className="jrn__offer-confirmed-note">
            We can't wait to have you with us. Watch your email and Messages tab for preparation details.
          </p>
          <Link to="/portal/book" className="jrn__offer-link">View or update my booking <HiOutlineArrowRight/></Link>
        </div>
      )}

      {/* ── Retreat completed — attended and now eligible again ── */}
      {offer?.queue_status === "alumni" && (
        <div className="pc jrn__offer-card jrn__offer-card--confirmed">
          <div className="jrn__offer-icon jrn__offer-icon--confirmed"><HiOutlineSparkles/></div>
          <p className="jrn__offer-eyebrow">Retreat Completed</p>
          <h2 className="jrn__offer-title">{offer.last_block_name || "Your Retreat"}</h2>
          {offer.last_block_start_date && (
            <p className="jrn__offer-dates">
              <HiOutlineCalendar/> {fmt(offer.last_block_start_date)} – {fmt(offer.last_block_end_date)}
            </p>
          )}
          {offer.last_block_location && (
            <p className="jrn__offer-location"><HiOutlineMapPin/> {offer.last_block_location}</p>
          )}
          <p className="jrn__offer-confirmed-note">
            Thank you for being part of Forge. We hope your retreat was meaningful —
            we'd love to have you join us again down the road.
          </p>
        </div>
      )}

      {/* ── In queue, waiting ── */}
      {offer?.queue_status === "queued" && (
        <div className="pc jrn__queue-card">
          <HiOutlineQueueList className="jrn__queue-icon"/>
          <div>
            <p className="jrn__queue-title">You're in the Retreat Queue</p>
            <p className="jrn__queue-sub">
              You've been fully cleared and are waiting for the next available retreat spot.
              We'll email you the moment one opens up.
            </p>
          </div>
        </div>
      )}

      {/* Progress timeline */}
      <div className="pc jrn__timeline-card">
        <p className="p-section-title">Your Progress</p>
        {loading ? (
          <div style={{ padding: "2rem", textAlign: "center", color: "#6b6258" }}>Loading…</div>
        ) : (
          <div className="jrn__timeline">
            {journey.map((step, i) => (
              <div key={step.key} className="jrn__tl-item">
                {/* Left — dot + connector line */}
                <div className="jrn__tl-track">
                  <div className={`jrn__tl-dot ${step.done ? "jrn__tl-dot--done" : step.active ? "jrn__tl-dot--active" : "jrn__tl-dot--pending"}`}>
                    {step.done
                      ? <HiOutlineCheck className="jrn__tl-check"/>
                      : <span className="jrn__tl-num">{i + 1}</span>}
                  </div>
                  {i < journey.length - 1 && (
                    <div className={`jrn__tl-line ${step.done ? "jrn__tl-line--done" : ""}`}/>
                  )}
                </div>
                {/* Right — label */}
                <div className={`jrn__tl-label ${step.active ? "jrn__tl-label--active" : ""}`}>
                  <p className={`jrn__tl-name ${step.active ? "jrn__tl-name--active" : ""} ${step.done ? "jrn__tl-name--done" : ""}`}>
                    {step.label}
                  </p>
                  <p className="jrn__tl-sub">{step.sub}</p>
                  {step.date && (
                    <p className="jrn__tl-date">
                      <HiOutlineCalendar /> {step.date}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Current step + what next */}
      <div className="jrn__lower">
        <div className="pc jrn__current">
          <p className="p-section-title">Current Status</p>
          <div className="jrn__current-icon">
            <div className="p-icon-circle p-icon-circle--rust" style={{ width: "4rem", height: "4rem", fontSize: "1.6rem" }}>
              <HiOutlineDocumentText />
            </div>
          </div>
          <p className="jrn__current-title">{activeStep.label}</p>
          <p className="jrn__current-desc">{activeStep.sub}</p>
          {app?.appRef && (
            <div className="jrn__ref-chip">
              <HiOutlineClock /> Ref: <strong>{app.appRef}</strong>
              {app.created_at && <span> · Submitted {fmt(app.created_at)}</span>}
            </div>
          )}
          <div className="jrn__current-note">
            <HiOutlineInformationCircle />
            <span>You'll be notified via email and in your Messages tab when there are updates.</span>
          </div>
        </div>

        <div className="pc jrn__next-card">
          <p className="p-section-title">What Happens Next?</p>
          <div className="jrn__next-list">
            <div className="jrn__next-item">
              <div className="p-icon-circle p-icon-circle--surface" style={{ width: "2.4rem", height: "2.4rem", fontSize: "0.9rem" }}>
                <HiOutlineDocumentText />
              </div>
              <div className="jrn__next-body">
                <p className="jrn__next-title">{activeStep.label}</p>
                <p className="jrn__next-desc">{guidance.desc}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom support bar */}
      <div className="pc jrn__bottom">
        <div className="jrn__support">
          <HiOutlineSparkles />
          <div>
            <p className="jrn__support-title">Need Support?</p>
            <p className="jrn__support-sub">If you have any questions or need help, our team is here for you.</p>
            <a href="mailto:support@forge.ngo" className="p-btn p-btn--outline" style={{ marginTop: "0.6rem", display: "inline-flex" }}>
              Contact Support <HiOutlineArrowRight />
            </a>
          </div>
          <img src="./assets/mountainsForst.png" alt="" className="jrn__bottom-mtn" />
        </div>
      </div>
    </PortalLayout>
  );
}
